import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { Formik } from 'formik';
import SubmitButton from './SubmitButton';
import Label from './Label';
import AddressLookup, { Address } from './AddressLookup';
import DayPickerInputValue from './edit/DayPickerInputValue';
import { CalendarIcon } from '@heroicons/react/24/solid';
import { useDateDisabler } from '../utilities/DateDisabler';
import { axiosKK } from '../services/networkRequest';
import {
  OrderBoxesResponse,
  BackendShippingAddress,
  CreateDPDLabelRequest,
  CreateDPDLabelResponse,
  DPDLabel,
  DPDService,
  ListDPDLabelsResponse,
  ListDPDServicesResponse,
  VoidDPDLabelResponse,
} from '../models/APIModels';
import Loading from './Loading';
import moment from 'moment';

interface DPDShippingModalProps {
  isOpen: boolean;
  close: () => void;
  collectionAddress: string;
  deliveryAddress: string;
  orderBoxes?: OrderBoxesResponse[];
  orderId: number;
  customer?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
  };
}

interface DPDFormValues {
  numberOfParcels: number;
  totalWeight: number;
  collectionDate?: Date;
  deliveryInstructions: string;
  shippingRef1: string;
  networkCode: string;
  collectionAddress: BackendShippingAddress;
  deliveryAddress: BackendShippingAddress;
  customCollectionAddress: Address;
  customDeliveryAddress: Address;
}

interface DPDFormErrors {
  numberOfParcels?: string;
  totalWeight?: string;
  collectionDate?: string;
  deliveryInstructions?: string;
  shippingRef1?: string;
  collectionAddress?: string;
  deliveryAddress?: string;
}

const DEFAULT_NETWORK_CODE = '2^12';

const KIT_KEEPER_WAREHOUSE_ADDRESS: BackendShippingAddress = {
  contactName: 'Michael McCreadie',
  organisationName: 'KIT KEEPER LTD - ARDINGTON ARCHIVE',
  addressLine1: 'ARDINGTON ARCHIVE',
  addressLine2: 'WHITE HORSE BUSINESS PARK/WARE ROAD',
  town: 'FARINGDON',
  county: 'Oxfordshire',
  postcode: 'SN7 8NY',
  countryCode: 'GB',
  phone: '',
  email: 'warehouse@kitkeeper.co.uk',
};

const parseAddressString = (
  addressString: string,
): Partial<BackendShippingAddress> => {
  const parts = addressString.split(', ').filter((p) => p.trim());
  return {
    addressLine1: parts[0] || '',
    addressLine2: parts.length > 3 ? parts[1] : '',
    town: parts[parts.length - 3] || '',
    postcode: parts[parts.length - 2] || '',
    countryCode: 'GB',
  };
};

const addressToLookup = (addr: BackendShippingAddress): Address => ({
  line1: addr.addressLine1,
  line2: addr.addressLine2,
  city: addr.town,
  county: addr.county,
  postCode: addr.postcode,
  country: addr.countryCode,
});

const lookupToAddress = (
  lookup: Address,
  base: BackendShippingAddress,
): BackendShippingAddress => ({
  ...base,
  addressLine1: lookup.line1 || '',
  addressLine2: lookup.line2 || '',
  town: lookup.city || '',
  county: lookup.county || '',
  postcode: lookup.postCode || '',
  countryCode: lookup.country || 'GB',
});

const printDpdLabel = (labelHtml: string) => {
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(labelHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }
};

const customStyles = {
  content: {
    display: 'flex',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
};

export default function DPDShippingModal({
  isOpen,
  close,
  collectionAddress,
  deliveryAddress,
  orderBoxes,
  orderId,
  customer,
}: DPDShippingModalProps): JSX.Element {
  const [existingLabels, setExistingLabels] = useState<DPDLabel[]>([]);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [createdLabel, setCreatedLabel] = useState<DPDLabel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [availableServices, setAvailableServices] = useState<DPDService[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [voidingLabelId, setVoidingLabelId] = useState<number | null>(null);

  const disabledDays = useDateDisabler(null, null, 1);

  const parsedDelivery = parseAddressString(deliveryAddress);
  const initialDeliveryAddress: BackendShippingAddress = {
    contactName:
      `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim() || '',
    organisationName: '',
    addressLine1: parsedDelivery.addressLine1 || '',
    addressLine2: parsedDelivery.addressLine2 || '',
    town: parsedDelivery.town || '',
    county: '',
    postcode: parsedDelivery.postcode || '',
    countryCode: 'GB',
    phone: customer?.phoneNumber || '',
    email: customer?.email || '',
  };

  const initialValues: DPDFormValues = {
    numberOfParcels: orderBoxes?.length || 1,
    totalWeight: 0,
    collectionDate: undefined,
    deliveryInstructions: '',
    shippingRef1: `ORDER-${orderId}`.substring(0, 25),
    networkCode: DEFAULT_NETWORK_CODE,
    collectionAddress: { ...KIT_KEEPER_WAREHOUSE_ADDRESS },
    deliveryAddress: initialDeliveryAddress,
    customCollectionAddress: addressToLookup(KIT_KEEPER_WAREHOUSE_ADDRESS),
    customDeliveryAddress: addressToLookup(initialDeliveryAddress),
  };

  useEffect(() => {
    if (isOpen) {
      setLoadingLabels(true);
      setError(null);
      setCreatedLabel(null);

      axiosKK
        .get<ListDPDLabelsResponse>(
          `/admin/order/${orderId}/shipping/labels`,
        )
        .then(({ data }) => {
          setExistingLabels(data.labels || []);
        })
        .catch((err) => {
          console.error('Failed to fetch existing labels:', err);
        })
        .finally(() => {
          setLoadingLabels(false);
        });
    }
  }, [isOpen, orderId]);

  const fetchServices = (collectionPostcode: string, deliveryPostcode: string) => {
    if (!collectionPostcode || !deliveryPostcode) return;
    setLoadingServices(true);
    axiosKK
      .get<ListDPDServicesResponse>('/admin/shipping/services', {
        params: { collectionPostcode, deliveryPostcode },
      })
      .then(({ data }) => {
        setAvailableServices(data.services || []);
      })
      .catch((err) => {
        console.error('Failed to fetch DPD services:', err);
        setAvailableServices([]);
      })
      .finally(() => {
        setLoadingServices(false);
      });
  };

  useEffect(() => {
    if (isOpen) {
      fetchServices(
        KIT_KEEPER_WAREHOUSE_ADDRESS.postcode,
        initialDeliveryAddress.postcode,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleVoidLabel = (labelId: number) => {
    if (!window.confirm('Are you sure you want to void this label?')) return;
    setVoidingLabelId(labelId);
    axiosKK
      .post<VoidDPDLabelResponse>(`/admin/shipping/label/${labelId}/void`)
      .then(({ data }) => {
        setExistingLabels((prev) =>
          prev.map((l) => (l.id === labelId ? data.label : l)),
        );
      })
      .catch((err) => {
        console.error('Failed to void label:', err);
        setError('Failed to void label');
      })
      .finally(() => {
        setVoidingLabelId(null);
      });
  };

  const handlePrintExistingLabel = (labelId: number) => {
    axiosKK
      .get<{ labelHtml: string }>(`/admin/shipping/label/${labelId}/print`)
      .then(({ data }) => {
        printDpdLabel(data.labelHtml);
      })
      .catch((err) => {
        console.error('Failed to fetch label for printing:', err);
        setError('Failed to fetch label for printing');
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={close}
      style={customStyles}
      contentLabel="DPD Shipping Modal"
      ariaHideApp={false}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validate={(values) => {
          const errors: DPDFormErrors = {};

          if (!values.numberOfParcels || values.numberOfParcels < 1) {
            errors.numberOfParcels = 'Must be at least 1';
          } else if (values.numberOfParcels > 99) {
            errors.numberOfParcels = 'Must be 99 or fewer';
          }

          if (!values.totalWeight || values.totalWeight <= 0) {
            errors.totalWeight = 'Weight must be greater than 0';
          } else if (values.totalWeight > 99999) {
            errors.totalWeight = 'Weight must be 99999 or less';
          }

          if (!values.collectionDate) {
            errors.collectionDate = 'Please select a collection date';
          }

          if (!values.collectionAddress.addressLine1) {
            errors.collectionAddress = 'Collection address line 1 is required';
          } else if (!values.collectionAddress.town) {
            errors.collectionAddress = 'Collection town is required';
          } else if (!values.collectionAddress.postcode) {
            errors.collectionAddress = 'Collection postcode is required';
          }

          if (!values.deliveryAddress.contactName) {
            errors.deliveryAddress = 'Delivery contact name is required';
          } else if (!values.deliveryAddress.addressLine1) {
            errors.deliveryAddress = 'Delivery address line 1 is required';
          } else if (!values.deliveryAddress.town) {
            errors.deliveryAddress = 'Delivery town is required';
          } else if (!values.deliveryAddress.postcode) {
            errors.deliveryAddress = 'Delivery postcode is required';
          } else if (!values.deliveryAddress.phone) {
            errors.deliveryAddress = 'Delivery phone is required';
          } else if (!values.deliveryAddress.email) {
            errors.deliveryAddress = 'Delivery email is required';
          }

          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          setError(null);

          const formattedDate = moment(values.collectionDate).format(
            'YYYY-MM-DD',
          );
          const collectionDateStr = `${formattedDate}T09:00:00`;

          const requestData: CreateDPDLabelRequest = {
            orderId,
            collectionAddress: values.collectionAddress,
            deliveryAddress: values.deliveryAddress,
            numberOfParcels: values.numberOfParcels,
            totalWeight: values.totalWeight,
            networkCode: values.networkCode,
            collectionDate: collectionDateStr,
            shippingRef1: values.shippingRef1,
            deliveryInstructions: values.deliveryInstructions,
          };

          axiosKK
            .post<CreateDPDLabelResponse>(
              '/admin/shipping/label',
              requestData,
            )
            .then(({ data }) => {
              setCreatedLabel(data.label);
              setExistingLabels((prev) => [data.label, ...prev]);
              setSubmitting(false);
            })
            .catch((err) => {
              console.error('Failed to create DPD label:', err);
              setError(
                err.response?.data?.error ||
                  'Failed to create DPD shipping label',
              );
              setSubmitting(false);
            });
        }}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          errors,
          touched,
          isSubmitting,
          isValid,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit} className="dpd-shipping-form">
            <div className="modal-header">
              <h3>DPD Shipping Label</h3>
              <SubmitButton red={true} type="button" onClick={close}>
                Close
              </SubmitButton>
            </div>

            <div className="modal-content">
              {loadingLabels && (
                <div className="loading-message">
                  <p>Loading existing labels...</p>
                  <Loading />
                </div>
              )}

              {error && (
                <div className="error-message">
                  <span className="error">{error}</span>
                </div>
              )}

              {/* Existing Labels Table */}
              {existingLabels.length > 0 && !createdLabel && (
                <div className="existing-labels">
                  <h4>Existing Labels</h4>
                  <table className="labels-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Tracking #</th>
                        <th>Parcel #</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {existingLabels.map((label) => (
                        <tr key={label.id}>
                          <td>
                            {moment(label.createdAt).format('DD/MM/YYYY')}
                          </td>
                          <td>{label.trackingNumber}</td>
                          <td>{label.parcelNumber}</td>
                          <td>{label.status}</td>
                          <td className="label-actions-cell">
                            <SubmitButton
                              type="button"
                              onClick={() => handlePrintExistingLabel(label.id)}
                            >
                              Print
                            </SubmitButton>
                            {label.status !== 'voided' && (
                              <SubmitButton
                                red={true}
                                type="button"
                                disabled={voidingLabelId === label.id}
                                onClick={() => handleVoidLabel(label.id)}
                              >
                                {voidingLabelId === label.id ? 'Voiding...' : 'Void'}
                              </SubmitButton>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Success State */}
              {createdLabel && (
                <div className="shipping-label-success">
                  <h4>DPD Label Created Successfully!</h4>
                  <div className="label-details">
                    <p>
                      <strong>Tracking Number:</strong>{' '}
                      {createdLabel.trackingNumber}
                    </p>
                    <p>
                      <strong>Parcel Number:</strong>{' '}
                      {createdLabel.parcelNumber}
                    </p>
                    <p>
                      <strong>Shipment ID:</strong> {createdLabel.shipmentId}
                    </p>
                  </div>
                  <div className="label-actions">
                    <SubmitButton
                      type="button"
                      onClick={() => {
                        if (createdLabel.labelData) {
                          printDpdLabel(createdLabel.labelData);
                        } else {
                          handlePrintExistingLabel(createdLabel.id);
                        }
                      }}
                    >
                      Print Label
                    </SubmitButton>
                  </div>
                </div>
              )}

              {/* Form - hidden when label just created */}
              {!createdLabel && !loadingLabels && (
                <>
                  <div className="form-group">
                    <Label htmlFor="numberOfParcels">Number of Parcels</Label>
                    <input
                      id="numberOfParcels"
                      name="numberOfParcels"
                      type="number"
                      min="1"
                      max="99"
                      value={values.numberOfParcels}
                      onChange={handleChange}
                      className="form-input"
                    />
                    {touched.numberOfParcels && errors.numberOfParcels && (
                      <span className="error">{errors.numberOfParcels}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="totalWeight">Total Weight (kg)</Label>
                    <input
                      id="totalWeight"
                      name="totalWeight"
                      type="number"
                      min="0.1"
                      step="0.1"
                      max="99999"
                      value={values.totalWeight}
                      onChange={handleChange}
                      className="form-input"
                    />
                    {touched.totalWeight && errors.totalWeight && (
                      <span className="error">{errors.totalWeight}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <div className="title-and-input">
                      <div className="title">
                        <Label>Collection Date:</Label>
                        <CalendarIcon
                          style={{
                            width: '30px',
                            height: '30px',
                            color: '#6c3cd9',
                          }}
                        />
                      </div>
                      <DayPickerInputValue
                        id="dpdCollectionDate"
                        handleStateChange={(day) =>
                          setFieldValue('collectionDate', day)
                        }
                        disabled={false}
                        disabledDays={disabledDays}
                      />
                    </div>
                    {touched.collectionDate && errors.collectionDate && (
                      <span className="error">{errors.collectionDate}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="shippingRef1">
                      Shipping Reference (max 25 chars)
                    </Label>
                    <input
                      id="shippingRef1"
                      name="shippingRef1"
                      type="text"
                      maxLength={25}
                      value={values.shippingRef1}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="deliveryInstructions">
                      Delivery Instructions (max 50 chars)
                    </Label>
                    <input
                      id="deliveryInstructions"
                      name="deliveryInstructions"
                      type="text"
                      maxLength={50}
                      value={values.deliveryInstructions}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="networkCode">DPD Service</Label>
                    <select
                      id="networkCode"
                      name="networkCode"
                      value={values.networkCode}
                      onChange={handleChange}
                      className="form-input dpd-service-select"
                      disabled={loadingServices}
                    >
                      {availableServices.length > 0 ? (
                        availableServices.map((service) => (
                          <option key={service.networkCode} value={service.networkCode}>
                            {service.networkDescription} ({service.networkCode})
                          </option>
                        ))
                      ) : (
                        <option value={DEFAULT_NETWORK_CODE}>
                          DPD Local Parcel Next Day (2^12){loadingServices ? ' — loading services...' : ''}
                        </option>
                      )}
                    </select>
                  </div>

                  {/* Collection Address */}
                  <div className="form-group address-section">
                    <h4>Collection Address (From)</h4>
                    <div className="address-fields">
                      <div className="address-field">
                        <Label htmlFor="collectionContactName">
                          Contact Name
                        </Label>
                        <input
                          id="collectionContactName"
                          type="text"
                          value={values.collectionAddress.contactName}
                          onChange={(e) =>
                            setFieldValue(
                              'collectionAddress.contactName',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                      <div className="address-field">
                        <Label htmlFor="collectionOrg">Organisation</Label>
                        <input
                          id="collectionOrg"
                          type="text"
                          value={values.collectionAddress.organisationName}
                          onChange={(e) =>
                            setFieldValue(
                              'collectionAddress.organisationName',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                      <div className="address-field">
                        <Label htmlFor="collectionPhone">Phone</Label>
                        <input
                          id="collectionPhone"
                          type="text"
                          value={values.collectionAddress.phone}
                          onChange={(e) =>
                            setFieldValue(
                              'collectionAddress.phone',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                      <div className="address-field">
                        <Label htmlFor="collectionEmail">Email</Label>
                        <input
                          id="collectionEmail"
                          type="text"
                          value={values.collectionAddress.email}
                          onChange={(e) =>
                            setFieldValue(
                              'collectionAddress.email',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div className="custom-address-input">
                      <AddressLookup
                        address={values.customCollectionAddress}
                        setAddress={(address: Address) => {
                          setFieldValue('customCollectionAddress', address);
                          setFieldValue(
                            'collectionAddress',
                            lookupToAddress(
                              address,
                              values.collectionAddress,
                            ),
                          );
                        }}
                        onlyUnitedKingdom
                        idPrefix="dpd-from"
                      />
                    </div>
                    {touched.collectionAddress && errors.collectionAddress && (
                      <span className="error">{errors.collectionAddress}</span>
                    )}
                  </div>

                  {/* Delivery Address */}
                  <div className="form-group address-section">
                    <h4>Delivery Address (To)</h4>
                    <div className="address-fields">
                      <div className="address-field">
                        <Label htmlFor="deliveryContactName">
                          Contact Name *
                        </Label>
                        <input
                          id="deliveryContactName"
                          type="text"
                          value={values.deliveryAddress.contactName}
                          onChange={(e) =>
                            setFieldValue(
                              'deliveryAddress.contactName',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                      <div className="address-field">
                        <Label htmlFor="deliveryOrg">Organisation</Label>
                        <input
                          id="deliveryOrg"
                          type="text"
                          value={values.deliveryAddress.organisationName}
                          onChange={(e) =>
                            setFieldValue(
                              'deliveryAddress.organisationName',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                      <div className="address-field">
                        <Label htmlFor="deliveryPhone">Phone *</Label>
                        <input
                          id="deliveryPhone"
                          type="text"
                          value={values.deliveryAddress.phone}
                          onChange={(e) =>
                            setFieldValue(
                              'deliveryAddress.phone',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                      <div className="address-field">
                        <Label htmlFor="deliveryEmail">Email *</Label>
                        <input
                          id="deliveryEmail"
                          type="text"
                          value={values.deliveryAddress.email}
                          onChange={(e) =>
                            setFieldValue(
                              'deliveryAddress.email',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div className="custom-address-input">
                      <AddressLookup
                        address={values.customDeliveryAddress}
                        setAddress={(address: Address) => {
                          setFieldValue('customDeliveryAddress', address);
                          setFieldValue(
                            'deliveryAddress',
                            lookupToAddress(
                              address,
                              values.deliveryAddress,
                            ),
                          );
                        }}
                        onlyUnitedKingdom
                        idPrefix="dpd-to"
                      />
                    </div>
                    {touched.deliveryAddress && errors.deliveryAddress && (
                      <span className="error">{errors.deliveryAddress}</span>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              {createdLabel ? (
                <SubmitButton type="button" onClick={close}>
                  Close
                </SubmitButton>
              ) : (
                <SubmitButton disabled={!isValid} isSubmitting={isSubmitting}>
                  Create DPD Label
                </SubmitButton>
              )}
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
}
